/**
 * PATH: packages/email-hub/src/feature/company-context/companyContextApi.ts
 * FILE_ID: EMAILHUB-COMPANYCONTEXT-API-001
 * PURPOSE: Typed I/O and tenant-aware cache identity for company context.
 * WHY: Domain alone is not a cache key: the same domain can resolve to
 *      different CRM records in different active organisations.
 */
import { emailApi } from '../lib/api'

export interface CompanyContext {
  domain: string
  company: {
    client_id: number | null
    client_name: string
    suggested_label: string
    crm_path: string | null
  }
  identity: {
    primary_role: string
    category_hint: string
    roles: Array<{ role: string; role_label?: string; confidence?: number; evidence_source?: string }>
    senders: string[]
    last_seen_at: string | null
    provenance: string
  }
  people: Array<{
    person: { id: string; display_name: string; primary_email: string }
    roles: Array<{ role: string; confidence?: number; evidence_source?: string }>
    emails: { count: number }
    documents: { count: number }
    jobs: Array<{ job_id: number; label: string; email_count: number }>
  }>
  work: {
    jobs: Array<{ job_id: number; label: string; email_count: number }>
    document_count: number
  }
  activity: {
    visible_email_count: number
    recent_emails: Array<{ id: string; subject: string; received_at: string; job_id: number | null }>
  }
}

export function companyContextKey(tenantId: number | null, domain: string) {
  return [
    'email-company-context',
    tenantId ?? 'no-active-tenant',
    domain.trim().toLowerCase(),
  ] as const
}

export async function fetchCompanyContext(domain: string): Promise<CompanyContext> {
  return emailApi<CompanyContext>(
    `/api/kanban/company-context/?domain=${encodeURIComponent(domain)}`,
  )
}
